/**
 * A team's members with their roles. Owners change roles and remove people; anyone may leave
 * (the last owner stays: the server says so).
 */
import { For, Show, type JSX } from "solid-js";
import type { Role } from "@protocol/Role";
import type { TeamView } from "@protocol/TeamView";
import { ROLES } from "../state/access";
import { ArmedButton } from "../shared/ui";
import { usePages } from "./context";

/** The members of `team`. */
export function MembersList(props: { team: TeamView }): JSX.Element {
  const { accounts, server, go } = usePages();
  const me = () => accounts.me(server)?.account.id;
  const owner = () => props.team.role === "owner";
  /** Removes `member` (or leaves, when it is you: then back to your teams). */
  const remove = async (member: string) => {
    const leaving = member === me();
    await accounts.removeMember(server, props.team.id, member);
    if (leaving && !accounts.me(server)?.teams.some((t) => t.id === props.team.id)) go({ page: "home" });
  };
  return (
    <section class="team-section">
      <h3>Members ({props.team.members.length})</h3>
      <ul class="member-list">
        <For each={props.team.members}>
          {(member) => (
            <li data-member={member.id} data-member-role={member.role}>
              <span class="member-name">
                <b>{member.name}</b>
                <Show when={member.id === me()}>
                  <span class="muted small"> (you)</span>
                </Show>
                <span class="mono small muted">{member.email}</span>
              </span>
              <Show when={owner()} fallback={<span class="role-pill">{member.role}</span>}>
                <select class="field" data-member-select value={member.role} onChange={(e) => void accounts.setRole(server, props.team.id, member.id, e.currentTarget.value as Role)}>
                  <For each={ROLES}>{(r) => <option value={r}>{r}</option>}</For>
                </select>
              </Show>
              <Show when={owner() || member.id === me()}>
                <ArmedButton
                  class="mini"
                  label={member.id === me() ? "Leave" : "Remove"}
                  armedLabel={member.id === me() ? "Click again to leave" : "Click again to remove"}
                  onConfirm={() => void remove(member.id)}
                />
              </Show>
            </li>
          )}
        </For>
      </ul>
    </section>
  );
}
